/**
 * Sidebar Component
 * Navigation sidebar with role-based menu items.
 * Supports parent items with children (dropdown).
 */

import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useUser } from '@store/authStore';
import { getNavigationByRole, type NavItemConfig } from '../../config/navigation';
import { getIcon, SearchIcon } from './icons';
import styles from './Sidebar.module.css';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onToggle: () => void;
}

/**
 * Sidebar component with navigation menu.
 */
export const Sidebar = ({ isOpen, onClose, onToggle }: SidebarProps) => {
  const location = useLocation();
  const user = useUser();
  const userRole = user?.role || 'daie';

  // Get navigation items based on user role
  const navigationItems = getNavigationByRole(userRole);

  // Separate main nav items from AI Assistant (bottom item)
  const mainNavItems = navigationItems.filter((item) => item.id !== 'ai-assistant');
  const bottomNavItems = navigationItems.filter((item) => item.id === 'ai-assistant');

  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    const path = location.pathname;
    const open: string[] = [];
    navigationItems.forEach((item) => {
      if (item.children?.some((c) => path === c.path || path.startsWith(c.path + '/'))) {
        open.push(item.id);
      }
    });
    return new Set(open);
  });

  useEffect(() => {
    const path = location.pathname;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      navigationItems.forEach((item) => {
        if (item.children?.some((c) => path === c.path || path.startsWith(c.path + '/'))) {
          next.add(item.id);
        }
      });
      return next.size === prev.size && [...next].every((id) => prev.has(id)) ? prev : next;
    });
  }, [location.pathname]);

  /**
   * Checks if a nav item is currently active.
   */
  const isActiveItem = (item: NavItemConfig): boolean => {
    return location.pathname === item.path || location.pathname.startsWith(item.path + '/');
  };

  /**
   * Checks if a child is the active one among its siblings.
   * When siblings have overlapping paths (e.g. /training and /training/my-programs),
   * only the most specific matching child is active.
   */
  const isActiveChild = (child: NavItemConfig, siblings: NavItemConfig[]): boolean => {
    const path = location.pathname;
    const childMatches = path === child.path || path.startsWith(child.path + '/');
    if (!childMatches) return false;
    const longerSiblingMatches = siblings.some(
      (s) => s.path.length > child.path.length && (path === s.path || path.startsWith(s.path + '/'))
    );
    return !longerSiblingMatches;
  };

  /**
   * Checks if any child of the item is active.
   */
  const hasActiveChild = (item: NavItemConfig): boolean => {
    return !!item.children?.some((c) => isActiveChild(c, item.children!));
  };

  /**
   * Handles navigation item click - closes sidebar on mobile
   */
  const handleNavClick = () => {
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /**
   * Renders a navigation item (link, section divider, or parent with children).
   */
  const renderNavItem = (item: NavItemConfig) => {
    if (item.sectionLabel) {
      return (
        <div key={item.id} className={styles.navSectionDivider} role="presentation">
          <span className={styles.navSectionLine} />
          <span className={styles.navSectionLabel}>{item.label}</span>
          <span className={styles.navSectionLine} />
        </div>
      );
    }

    const Icon = getIcon(item.icon);
    const isActive = isActiveItem(item);
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedIds.has(item.id);
    const isParentActive = hasChildren && hasActiveChild(item);

    if (hasChildren) {
      return (
        <div key={item.id} className={styles.navGroup}>
          <button
            type="button"
            onClick={() => toggleExpanded(item.id)}
            className={`${styles.navItem} ${styles.navItemButton} ${isParentActive ? styles.navItemActive : ''}`}
            aria-expanded={isExpanded}
          >
            {Icon && <Icon className={styles.navIcon} />}
            <span className={styles.navLabel}>{item.label}</span>
            <span className={`${styles.navChevron} ${isExpanded ? styles.navChevronOpen : ''}`} aria-hidden>
              ▼
            </span>
          </button>
          {isExpanded && (
            <div className={styles.navChildren}>
              {item.children!.map((child) => {
                const ChildIcon = getIcon(child.icon);
                const childActive = isActiveChild(child, item.children!);
                return (
                  <Link
                    key={child.id}
                    to={child.path}
                    onClick={handleNavClick}
                    className={`${styles.navItem} ${styles.navChild} ${childActive ? styles.navItemActive : ''}`}
                  >
                    {ChildIcon && <ChildIcon className={styles.navIcon} />}
                    <span className={styles.navLabel}>{child.label}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.id}
        to={item.path}
        onClick={handleNavClick}
        className={`${styles.navItem} ${isActive ? styles.navItemActive : ''}`}
        aria-current={isActive ? 'page' : undefined}
      >
        {Icon && <Icon className={styles.navIcon} />}
        <span className={styles.navLabel}>{item.label}</span>
      </Link>
    );
  };

  // Handle Escape key to close sidebar on mobile
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen, onClose]);

  return (
    <>
      {/* Burger Menu Button - Always visible */}
      <button
        className={`${styles.menuButton} ${isOpen ? styles.menuButtonOpen : ''}`}
        onClick={onToggle}
        aria-label="Toggle menu"
        aria-expanded={isOpen}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      <aside className={`${styles.sidebar} ${isOpen ? styles.sidebarOpen : ''}`}>
        {/* Logo Section */}
        <div className={styles.logoSection}>
          <div className={styles.logoHeader}>
            <h1 className={styles.logoText}>Dream System</h1>
          </div>
          <span className={styles.logoSubtext}>Performance Management</span>
        </div>

      {/* Search Box */}
      <div className={styles.searchBox}>
        <SearchIcon className={styles.searchIcon} />
        <input
          type="text"
          placeholder="Search..."
          className={styles.searchInput}
          aria-label="Search"
        />
      </div>

      {/* Main Navigation */}
      <nav className={styles.nav}>
        <div className={styles.navMain}>
          {mainNavItems.map(renderNavItem)}
        </div>

        {/* Bottom Navigation (AI Assistant) */}
        {bottomNavItems.length > 0 && (
          <div className={styles.navBottom}>
            {bottomNavItems.map(renderNavItem)}
          </div>
        )}
      </nav>
      </aside>
    </>
  );
};

